import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  CircularProgress,
  Alert,
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TablePagination,
  Link,
  Tab,
  Badge,
  Grid,
  Tooltip,
  IconButton,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import { lessonsAPI } from '../services/api';
import { toast } from 'react-toastify';
import Button from '../components/UIKit/Button';
import Tabs from '../components/UIKit/Tabs';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { formatDate } from '../utils/formatters';
import LessonChangeRequestModal from '../components/LessonChangeRequestModal';

// Получение цвета чипа статуса
function getStatusChipColor(status) {
  switch (status) {
    case 'pending':
      return 'warning';
    case 'approved':
      return 'success';
    case 'rejected':
      return 'error';
    default:
      return 'default';
  }
}

// Получение иконки статуса
function getStatusIcon(status) {
  switch (status) {
    case 'pending':
      return <AccessTimeIcon />;
    case 'approved':
      return <CheckCircleIcon />;
    case 'rejected':
      return <CancelIcon />;
    default:
      return null;
  }
}

const LessonChangeRequestsPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation(['admin', 'common']);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [changeRequests, setChangeRequests] = useState([]);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [filterStatus, setFilterStatus] = useState('all');
  const [counts, setCounts] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
  });
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // Проверка прав администратора
  useEffect(() => {
    if (!user || !user.roles?.includes('admin')) {
      navigate('/');
      toast.error(t('common:errors.accessDenied'));
    }
  }, [user, navigate, t]);

  // Загрузка данных запросов на изменение
  const fetchChangeRequests = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {
        page: page + 1,
        limit: rowsPerPage,
        status: filterStatus === 'all' ? undefined : filterStatus,
      };

      const response = await lessonsAPI.getAllChangeRequests(params);

      if (response && response.data) {
        setChangeRequests(response.data.data);
        setTotalCount(response.data.pagination.total);

        // Обновляем счетчики
        if (response.data.counts) {
          setCounts(response.data.counts);
        }
      } else {
        setError(t('admin:lessonChangeRequests.errors.loadError'));
      }
    } catch (err) {
      console.error('Ошибка при загрузке запросов на изменение:', err);
      setError(
        err.response?.data?.message ||
          t('admin:lessonChangeRequests.errors.loadError')
      );
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, filterStatus, t]);

  useEffect(() => {
    fetchChangeRequests();
  }, [fetchChangeRequests]);

  // Обработчики пагинации
  const handleChangePage = useCallback((event, newPage) => {
    setPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback((event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }, []);

  // Обработчик изменения фильтра по статусу
  const handleChangeTab = useCallback((event, newValue) => {
    setFilterStatus(newValue);
    setPage(0);
  }, []);

  // Открытие модального окна для просмотра запроса
  const handleViewRequest = useCallback((requestId) => {
    setSelectedRequestId(requestId);
    setModalOpen(true);
  }, []);

  // Закрытие модального окна
  const handleCloseModal = useCallback(() => {
    setModalOpen(false);
    setSelectedRequestId(null);
  }, []);

  // Обработчик после изменения запроса
  const handleRequestProcessed = useCallback(() => {
    // Обновляем список запросов
    fetchChangeRequests();
  }, [fetchChangeRequests]);

  return (
    <>
      <Grid
        container
        spacing={{ xs: 1, lg: 2 }}
        sx={{
          mb: 3,
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'center' },
        }}
      >
        <Grid item xs={12} lg>
          <Typography variant="h5" component="h1">
            {t('admin:lessonChangeRequests.title')}
          </Typography>
        </Grid>
        <Grid
          item
          xs={12}
          lg={6}
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: { xs: 'flex-start', md: 'flex-end' },
          }}
        >
          <Box
            sx={{
              bgcolor: 'background.default',
              borderRadius: { xs: '12px', sm: '16px' },
              p: '4px',
              width: '100%',
              overflow: 'hidden',
            }}
          >
            <Tabs
              value={filterStatus}
              onChange={handleChangeTab}
              aria-label="change-requests-tabs"
            >
              <Tab
                label={t('admin:lessonChangeRequests.tabs.all')}
                value="all"
              />
              <Tab
                label={
                  <Badge badgeContent={counts.pending} color="warning">
                    {t('admin:lessonChangeRequests.tabs.pending')}
                  </Badge>
                }
                value="pending"
              />
              <Tab
                label={
                  <Badge badgeContent={counts.approved} color="success">
                    {t('admin:lessonChangeRequests.tabs.approved')}
                  </Badge>
                }
                value="approved"
              />
              <Tab
                label={
                  <Badge badgeContent={counts.rejected} color="error">
                    {t('admin:lessonChangeRequests.tabs.rejected')}
                  </Badge>
                }
                value="rejected"
              />
            </Tabs>
          </Box>
        </Grid>
      </Grid>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 4 }}
          action={
            <Button
              variant="text"
              size="small"
              onClick={fetchChangeRequests}
            >
              {t('common:actions.retry')}
            </Button>
          }
        >
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : changeRequests.length === 0 ? (
        <Paper elevation={0} sx={{ p: 3, textAlign: 'center' }}>
          <InfoIcon color="info" sx={{ fontSize: 48, mb: 2 }} />
          <Typography variant="h6">
            {t('admin:lessonChangeRequests.noRequests')}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper} elevation={0}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>
                  {t('admin:lessonChangeRequests.table.lesson')}
                </TableCell>
                <TableCell>
                  {t('admin:lessonChangeRequests.table.requestedBy')}
                </TableCell>
                <TableCell>
                  {t('admin:lessonChangeRequests.table.date')}
                </TableCell>
                <TableCell>
                  {t('admin:lessonChangeRequests.table.status')}
                </TableCell>
                <TableCell align="right">
                  {t('admin:lessonChangeRequests.table.actions')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {changeRequests.map((request) => (
                <TableRow key={request._id} hover>
                  <TableCell>
                    <Link
                      href={`/lessons/${request.lesson.slug || request.lesson._id}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      underline="hover"
                    >
                      {request.lesson.title}
                    </Link>
                  </TableCell>
                  <TableCell>
                    <Box>
                      <Typography variant="body2">
                        {request.sender?.fullName || request.requestedBy?.userMeta?.fullName || request.requestedBy?.username || t('common:unknownUser')}
                      </Typography>
                      {request.sender?.email && (
                        <Typography variant="caption" color="text.secondary">
                          {request.sender.email}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{formatDate(request.createdAt)}</TableCell>
                  <TableCell>
                    <Chip
                      icon={getStatusIcon(request.status)}
                      label={t(
                        `admin:lessonChangeRequests.status.${request.status}`
                      )}
                      color={getStatusChipColor(request.status)}
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                      <Tooltip
                        title={t('admin:lessonChangeRequests.buttons.view')}
                      >
                        <IconButton
                          color="primary"
                          onClick={() => handleViewRequest(request._id)}
                          size="small"
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {loading && (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && changeRequests.length > 0 && (
        <TablePagination
          rowsPerPageOptions={[5, 10, 25]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          labelRowsPerPage={t('common:pagination.rowsPerPage')}
          labelDisplayedRows={({ from, to, count }) =>
            t('common:pagination.displayedRows', { from, to, count })
          }
        />
      )}

      <LessonChangeRequestModal
        open={modalOpen}
        onClose={handleCloseModal}
        requestId={selectedRequestId}
        onRequestProcessed={handleRequestProcessed}
      />
    </>
  );
};

export default LessonChangeRequestsPage;
